import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  MenuItem,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import PropTypes from "prop-types";
import { fieldStyles } from "services/utils";
import MDButton from "components/MDButton";
import { getCompetencyKpiLinks } from "services/api";
import { set } from "date-fns";

const KpiMappingDialog = ({ open, onClose, competency, onSave, allKpis }) => {
  // Array of { kpiId: number, weightage: number }
  const [mappings, setMappings] = useState([]);
  const [selectedKpiId, setSelectedKpiId] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchLinks = async () => {
      if (open && competency?.competencyId) {
        try {
          setLoading(true);

          const links = await getCompetencyKpiLinks(competency.competencyId);
          // Transform the backend data (CompetencyKpiModel)
          // to match your frontend state (mappings)
          const formattedMappings = links.map((link) => ({
            kpiId: link.id.kpiId,
            weightage: link.weightage,
          }));

          setMappings(formattedMappings);
          setLoading(false);
        } catch (error) {
          setLoading(false);
          console.error("Error fetching KPI links:", error);
        }
      } else {
        setLoading(false);
        // Reset mappings when dialog closes
        setMappings([]);
      }
    };

    fetchLinks();
  }, [open, competency]);

  const handleAddKpi = () => {
    if (selectedKpiId && !mappings.find((m) => m.kpiId === selectedKpiId)) {
      setMappings([...mappings, { kpiId: selectedKpiId, weightage: 100 }]);
      setSelectedKpiId("");
    }
  };

  const handleWeightChange = (index, value) => {
    const newMappings = [...mappings];
    const weight = Math.min(100, Math.max(1, parseInt(value) || 0));
    newMappings[index].weightage = weight;
    setMappings(newMappings);
  };

  const removeMapping = (index) => {
    setMappings(mappings.filter((_, i) => i !== index));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Connect KPIs to: {competency?.name}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", gap: 1, mt: 2, mb: 3, alignItems: "flex-start" }}>
          <Autocomplete
            fullWidth
            options={allKpis}
            // Filter out KPIs already in the mappings list
            getOptionDisabled={(option) => mappings.some((m) => m.kpiId === option.kpiNameId)}
            // Define what text is shown in the list and search
            getOptionLabel={(option) => option.kpiName || ""}
            // Controlled value logic
            value={allKpis.find((k) => k.kpiNameId === selectedKpiId) || null}
            onChange={(event, newValue) => {
              setSelectedKpiId(newValue ? newValue.kpiNameId : "");
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search & Select KPI"
                variant="outlined"
                sx={fieldStyles} // Applying your custom styles
              />
            )}
          />

          <MDButton
            variant="contained"
            onClick={handleAddKpi}
            disabled={!selectedKpiId}
            color="primary"
          >
            Add
          </MDButton>
        </Box>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Active KPI Connections
        </Typography>
        {loading ? (
          <Box display="flex" justifyContent="center" p={1}>
            <CircularProgress size={20} sx={{ color: "black" }} />
          </Box>
        ) : (
          <List>
            {mappings.map((mapping, index) => {
              const kpiName =
                allKpis.find((k) => k.kpiNameId === mapping.kpiId)?.kpiName || "Unknown KPI";
              return (
                <React.Fragment key={mapping.kpiId}>
                  <ListItem
                    secondaryAction={
                      <IconButton edge="end" onClick={() => removeMapping(index)}>
                        <DeleteIcon color="error" />
                      </IconButton>
                    }
                  >
                    <ListItemText primary={kpiName} sx={{ width: "60%" }} />
                    <TextField
                      type="number"
                      label="Weight %"
                      size="small"
                      value={mapping.weightage}
                      onChange={(e) => handleWeightChange(index, e.target.value)}
                      sx={{ width: 100, mr: 2 }}
                      inputProps={{ min: 1, max: 100 }}
                    />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              );
            })}
          </List>
        )}
      </DialogContent>
      {saving ? (
        <Box display="flex" justifyContent="flex-end" p={1}>
          <CircularProgress size={20} sx={{ color: "black" }} />
        </Box>
      ) : (
        <DialogActions>
          <MDButton color="secondary" onClick={onClose} variant="outlined">
            Cancel
          </MDButton>
          <MDButton
            variant="contained"
            color="success"
            onClick={async () => {
              setSaving(true);
              await onSave(competency.competencyId, mappings);
              setSaving(false);
            }}
          >
            Save Connections
          </MDButton>
        </DialogActions>
      )}
    </Dialog>
  );
};

KpiMappingDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  competency: PropTypes.object.isRequired, // { competencyId, name, kpiMappings: [{ kpiId, weightage }] }
  onSave: PropTypes.func.isRequired, // (competencyId, mappings) => void
  allKpis: PropTypes.array.isRequired, // [{ kpiNameId, kpiName }]
};

export default KpiMappingDialog;
